import React, { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';

/** Will animate itself out when it touches the edge of the root element, using an intersection observer. */
function EllipsisItem({
  id,
  children,
  parentRef,
  onVisibilityChange,
}: {
  id: string;
  children: React.ReactNode;
  parentRef: React.RefObject<HTMLDivElement | null>;
  onVisibilityChange?: (isVisible: boolean) => void;
}) {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const itemRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!itemRef.current || !parentRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isFullyVisible = entry.intersectionRatio >= 1.0;
        setIsOverflowing(!isFullyVisible);
        onVisibilityChange?.(!isFullyVisible);
      },
      { root: parentRef.current, rootMargin: '2px', threshold: 1.0 }
    );
    observer.observe(itemRef.current);
    return () => {
      if (itemRef.current) {
        observer.unobserve(itemRef.current);
      }
      observer.disconnect();
    };
  }, [parentRef, itemRef]);

  const element = children as React.ReactElement<{
    className?: string;
    id?: string;
    ref?: React.Ref<HTMLLIElement>;
  }>;
  return React.cloneElement(element, {
    className: clsx(
      'w-fit shrink-0 translate-y-0 overflow-hidden whitespace-nowrap transition-all',
      { 'opacity-0': isOverflowing },
      element.props.className
    ),
    id: id,
    ref: itemRef,
  });
}

/** This component takes an array of children and renders them with a custom ellipsis fallback if they overflow. */
export function Ellipsis({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  // Count number of items that are overflowing
  const [overflowCount, setOverflowCount] = useState<Record<number, boolean>>(
    {}
  );
  const count = Object.values(overflowCount).filter((v) => v).length;

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div
        ref={containerRef}
        className={clsx('overflow-hidden transition-all', className)}
      >
        {React.Children.map(children, (child, index) => {
          return (
            <EllipsisItem
              id={`ellipsis-item-${index}`}
              parentRef={containerRef}
              // eslint-disable-next-line @typescript-eslint/no-base-to-string
              key={child?.toString()}
              onVisibilityChange={(isVisible) => {
                setOverflowCount((prev) => {
                  return {
                    ...prev,
                    [index]: isVisible,
                  };
                });
              }}
            >
              {child}
            </EllipsisItem>
          );
        })}
      </div>
      {count > 0 && (
        <div className="flex h-full w-24 items-center justify-center self-center">
          <span className="text-neutral-dark-1">+{count}</span>
        </div>
      )}
    </>
  );
}
